import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@headlessui/react';
import { 
  FaPlus, FaEdit, FaTrash, FaEye, FaCopy, FaExternalLinkAlt, 
  FaChartLine, FaMobile, FaDesktop, FaGlobe, FaCog, FaPlay, FaPause,
  FaCheckCircle, FaExclamationCircle, FaClock, FaInfoCircle, FaTimes, FaAd,
  FaMousePointer, FaChartBar, FaFolder, FaQuestionCircle, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import { 
  getToken, 
  getUser, 
  isTokenValid, 
  redirectToLogin,
  authenticatedFetch,
  getBaseUrl,
  getUserLimits
} from '../utils/tokenUtils';
// UpgradeModal handled by parent App.js
import StatsCard from '../components/StatsCard';
import VideoModal from '../components/VideoModal';

const formatNumberCompact = (num) => {
  if (num === null || num === undefined) return '0';
  const val = Number(num);
  if (isNaN(val)) return String(num);
  if (val >= 1000000) {
    return (val / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (val >= 1000) {
    return (val / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return val.toLocaleString();
};

const InternalAds = ({ user, onLogout, onStatsUpdate, onShowFeedbackModal, onUpgradeClick }) => {
  const navigate = useNavigate();
  const [placements, setPlacements] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  // Modal states handled by parent App.js
  const [planLimits, setPlanLimits] = useState(null);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPlacements, setTotalPlacements] = useState(0);
  const [limit] = useState(10);
  
  // Toast notification states
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    fetchUserData();
  }, []);

  // Fetch fresh user data
  const fetchUserData = async () => {
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const response = await authenticatedFetch('/api/auth/me');
      if (response.ok) {
        const data = await response.json();
        if (data.user) {
          // Update localStorage with fresh user data
          localStorage.setItem('user', JSON.stringify(data.user));
          // Trigger parent component to update user state
          if (onStatsUpdate) {
            onStatsUpdate();
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  // Listen upgrade button from header
  useEffect(() => {
    const onHeaderUpgrade = (e) => {
      const section = e?.detail?.section || 'internalads';
      handleUpgradeClick(section);
    };
    window.addEventListener('open-upgrade-modal', onHeaderUpgrade);
    return () => window.removeEventListener('open-upgrade-modal', onHeaderUpgrade);
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };


  // Check if user can create more placements
  const canCreatePlacement = () => {
    const currentCount = placements.length;
    const limits = getUserLimits();
    
    if (!limits) {
      return false; // Limits henüz yüklenmemiş
    }
    
    const userPlan = limits.plan || 'free';
    const userLimit = limits.maxInternalAds;
    
    // Enterprise planında sınırsız (-1)
    if (userPlan === 'enterprise' || userLimit === -1) {
      return true;
    }
    
    // Kullanıcının limitini kullan, yoksa plan bazlı fallback
    if (userLimit !== undefined) {
      return currentCount < userLimit;
    }
    
    // Fallback limitler
    switch (userPlan) {
      case 'free':
        return currentCount < 0; // Free plan'da hiç internal ad oluşturamaz
      case 'premium':
        return currentCount < 5;
      default:
        return currentCount < 0;
    }
  };

  const getPlacementLimit = () => {
    const limits = getUserLimits();
    
    if (!limits) {
      return 0; // Default free plan limit
    }
    
    const userPlan = limits.plan || 'free';
    const userLimit = limits.maxInternalAds;
    
    // Enterprise planında sınırsız (-1)
    if (userPlan === 'enterprise' || userLimit === -1) {
      return 'Unlimited';
    }
    
    // Kullanıcının limitini kullan, yoksa plan bazlı fallback
    if (userLimit !== undefined) {
      return userLimit;
    }
    
    // Fallback limitler
    switch (userPlan) {
      case 'free':
        return 0;
      case 'premium':
        return 5;
      default:
        return 0;
    }
  };

  const handleUpgradeClick = (section = null) => {
    if (onUpgradeClick) {
      onUpgradeClick(section);
    }
  };

  // handleUpgradeSuccess handled by parent App.js


  const fetchData = async () => {
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const [placementsResponse, projectsResponse, limitsResponse] = await Promise.all([
        authenticatedFetch(`/api/internal-ads/placements?page=${currentPage}&limit=${limit}`),
        authenticatedFetch('/api/dashboard/user/projects?limit=0'),
        authenticatedFetch('/api/auth/plan-limits')
      ]);

      if (placementsResponse.ok) {
        const placementsData = await placementsResponse.json();
        setPlacements(placementsData.placements || []);
        setTotalPages(placementsData.totalPages || 1);
        setTotalPlacements(placementsData.total || 0);
      }

      if (projectsResponse.ok) {
        const projectsData = await projectsResponse.json();
        setProjects(projectsData.projects || []);
      }

      if (limitsResponse.ok) {
        const limitsData = await limitsResponse.json();
        setPlanLimits(limitsData.limits);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      showToast('Error loading data.', 'error');
      setLoading(false);
    }
  };

  const handleDeletePlacement = async (placementId) => {
    if (!window.confirm('Are you sure you want to delete this placement?')) {
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/internal-ads/placements/${placementId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showToast('Placement deleted successfully.');
        fetchData();
        if (onStatsUpdate) onStatsUpdate();
      } else {
        const error = await response.json();
        showToast(error.message || 'Delete operation failed.', 'error');
      }
    } catch (error) {
      console.error('Error deleting placement:', error);
      showToast('Error occurred during deletion.', 'error');
    }
  };

  const handleToggleStatus = async (placement) => {
    try {
      const newStatus = placement.status === 'active' ? 'inactive' : 'active';
      const response = await authenticatedFetch(`/api/internal-ads/placements/${placement._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        showToast(`Placement ${newStatus === 'active' ? 'activated' : 'deactivated'}.`);
        fetchData();
        if (onStatsUpdate) onStatsUpdate();
      } else {
        const error = await response.json();
        showToast(error.message || 'Status update failed.', 'error');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('Error occurred during status update.', 'error');
    }
  };

  const handleViewStats = (placement) => {
    navigate(`/dashboard/internal-ads/${placement._id}`);
  };

  const copyApiKey = (apiKey) => {
    navigator.clipboard.writeText(apiKey);
    showToast('API key copied to clipboard.');
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'android': return <FaMobile className="text-green-500" />;
      case 'ios': return <FaMobile className="text-blue-500" />;
      case 'web': return <FaGlobe className="text-purple-500" />;
      case 'all': return <FaDesktop className="text-gray-500" />;
      default: return <FaDesktop className="text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FaCheckCircle className="mr-1" />
          Active
        </span>;
      case 'inactive':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <FaExclamationCircle className="mr-1" />
          Inactive
        </span>;
      case 'paused':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <FaClock className="mr-1" />
          Paused
        </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Unknown
        </span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 left-4 right-4 lg:left-auto lg:right-4 lg:max-w-sm z-[60] px-4 lg:px-6 py-3 rounded-lg shadow-lg transition-all duration-300 text-sm lg:text-base ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      <div className="p-4 sm:p-6">
        {/* Analytics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <StatsCard
            title="Total Placements"
            value={placements.length}
            icon={FaAd}
            showLimit={true}
            current={placements.length}
            limit={(() => {
              const limits = getUserLimits();
              if (!limits) return 0;
              return limits.plan === 'enterprise' || limits.maxInternalAds === -1 ? -1 : (limits.maxInternalAds || 0);
            })()}
          />
          
          <StatsCard
            title="Total Impressions"
            value={placements.reduce((total, placement) => total + (placement.statistics?.totalImpressions || 0), 0)}
            icon={FaChartLine}
          />
          
          <StatsCard
            title="Total Clicks"
            value={placements.reduce((total, placement) => total + (placement.statistics?.totalClicks || 0), 0)}
            icon={FaMousePointer}
          />
          
          <StatsCard
            title="Avg CTR"
            value={`${placements.length > 0 
              ? (placements.reduce((total, placement) => total + (placement.statistics?.clickThroughRate || 0), 0) / placements.length).toFixed(1)
              : 0}%`}
            icon={FaChartBar}
          />
        </div>

        {/* Internal Ads Section */}
        <div className="bg-black rounded-xl border border-gray-800">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-4 sm:mb-6 space-y-4 lg:space-y-0">
              <h1 className="text-base font-semibold text-white">Internal Ads</h1>
              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowVideoModal(true)}
                  className="group flex items-center px-3 lg:px-4 py-2 lg:py-3 rounded-xl transition-all duration-200 bg-gray-800 text-white hover:bg-gray-700 border border-gray-700 data-hover:bg-gray-700 data-focus:ring-white text-sm lg:text-base"
                >
                  <FaQuestionCircle className="mr-1 lg:mr-2 h-3 w-3 lg:h-4 lg:w-4 transition-transform duration-200 group-hover:scale-110" />
                  <span className="hidden lg:inline">How to Create?</span>
                  <span className="lg:hidden">How to?</span>
                </Button>
                <Button
                  onClick={() => canCreatePlacement() ? setShowCreateModal(true) : handleUpgradeClick('internal-ads')}
                  className={`group flex items-center px-4 lg:px-6 py-2 lg:py-3 rounded-xl transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border text-sm lg:text-base ${
                    canCreatePlacement() 
                      ? 'bg-gray-800 text-white hover:bg-gray-700 border-gray-700' 
                      : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 border-indigo-500 animate-pulse'
                  }`}
                >
                  <FaPlus className={`mr-1 lg:mr-2 h-3 w-3 lg:h-4 lg:w-4 transition-transform duration-200 ${
                    canCreatePlacement() ? 'group-hover:rotate-90' : 'animate-bounce'
                  }`} />
                  <span className="hidden lg:inline">
                    {canCreatePlacement() ? 'New Placement' : 'Upgrade to Create More'}
                  </span>
                  <span className="lg:hidden">
                    {canCreatePlacement() ? 'New Placement' : 'Upgrade'}
                  </span>
                </Button>
              </div>
            </div>

            {placements.length === 0 ? (
              <div className="text-center py-8 lg:py-12">
                <FaAd className="mx-auto h-10 w-10 lg:h-12 lg:w-12 text-gray-400 mb-3 lg:mb-4" />
                <h3 className="text-base lg:text-lg font-medium text-white mb-2">No placements yet</h3>
                <p className="text-gray-400 mb-4 lg:mb-6 text-sm lg:text-base">Get started by creating your first placement</p>
                <div className="flex justify-center">
                  <Button
                    onClick={() => canCreatePlacement() ? setShowCreateModal(true) : handleUpgradeClick('internal-ads')}
                    className={`group flex items-center px-4 lg:px-6 py-2 lg:py-3 rounded-xl transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border text-sm lg:text-base ${
                      canCreatePlacement() 
                        ? 'bg-gray-800 text-white hover:bg-gray-700 border-gray-700' 
                        : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 border-indigo-500 animate-pulse'
                    }`}
                  >
                    <FaPlus className={`mr-1 lg:mr-2 h-3 w-3 lg:h-4 lg:w-4 transition-transform duration-200 ${
                      canCreatePlacement() ? 'group-hover:rotate-90' : 'animate-bounce'
                    }`} />
                    <span className="hidden lg:inline">
                      {canCreatePlacement() ? 'New Placement' : 'Upgrade to Create More'}
                    </span>
                    <span className="lg:hidden">
                      {canCreatePlacement() ? 'New Placement' : 'Upgrade'}
                    </span>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {placements.map((placement) => (
                  <div 
                    key={placement._id} 
                    className="bg-black rounded-lg border border-gray-800 hover:border-gray-700 transition-all duration-200 cursor-pointer"
                    onClick={() => handleViewStats(placement)}
                  >
                    <div className="p-4 lg:p-6">
                      <div className="flex items-start lg:items-center justify-between">
                        {/* Left Side - Main Info */}
                        <div className="flex items-center space-x-3 lg:space-x-4 flex-1 min-w-0 pr-2">
                          {/* Platform Icon with Status Dot */}
                          <div className="flex-shrink-0 w-8 h-8 lg:w-12 lg:h-12 relative">
                            {placement.projectId?.logo ? (
                              <img 
                                src={placement.projectId.logo.startsWith('http') ? placement.projectId.logo : `${getBaseUrl()}${placement.projectId.logo}`}
                                alt="Project Logo" 
                                className="w-8 h-8 lg:w-12 lg:h-12 rounded-xl object-cover border border-gray-600"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            <div 
                              className="w-8 h-8 lg:w-12 lg:h-12 bg-gray-800 rounded-xl flex items-center justify-center"
                              style={{ display: placement.projectId?.logo ? 'none' : 'flex' }}
                            >
                              {getPlatformIcon(placement.platform)}
                            </div>
                            {/* Status Indicator - Top Right of Icon */}
                            <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-gray-900 ${
                              placement.status === 'active' ? 'bg-green-400' : 'bg-gray-400'
                            }`}></div>
                          </div>

                          {/* Placement Info */}
                          <div className="flex flex-col space-y-1 flex-1 min-w-0">
                            {/* Title */}
                            <div className="text-sm text-gray-300 text-left truncate">
                              {placement.name}
                            </div>
                            
                            
                            {/* Project and Stats */}
                            <div className="flex flex-wrap items-center gap-2 lg:gap-3 text-xs text-gray-500">
                              <div className="flex items-center space-x-1">
                                <FaFolder className="h-3 w-3" />
                                <span className="truncate">{placement.projectId?.name}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <FaAd className="h-3 w-3" />
                                <span>{placement.selectedProjects?.length || 0} available projects</span>
                              </div>
                              <div className="flex items-center space-x-1" title={(placement.statistics?.totalImpressions || 0).toLocaleString()}>
                                <FaChartLine className="h-3 w-3" />
                                <span>{formatNumberCompact(placement.statistics?.totalImpressions || 0)} impressions</span>
                              </div>
                              <div className="flex items-center space-x-1" title={(placement.statistics?.totalClicks || 0).toLocaleString()}>
                                <FaMousePointer className="h-3 w-3" />
                                <span>{formatNumberCompact(placement.statistics?.totalClicks || 0)} clicks</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Desktop Actions */}
                        <div className="hidden lg:flex items-center space-x-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          {/* View Details Button */}
                          <button
                            onClick={() => handleViewStats(placement)}
                            className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
                            title="View details"
                          >
                            <FaChartLine className="mr-2" />
                            Details
                          </button>
                        </div>

                        {/* Mobile details button */}
                        <div className="lg:hidden flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleViewStats(placement)}
                            className="p-2 text-white hover:text-gray-300 transition-colors bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600"
                            title="View details"
                          >
                            <FaChartLine className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-between border-t border-gray-800 pt-6">
                    <div className="flex flex-1 justify-between sm:hidden">
                      <Button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-md border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                      >
                        Previous
                      </Button>
                      <Button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative ml-3 inline-flex items-center rounded-md border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                      >
                        Next
                      </Button>
                    </div>
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-400">
                          Showing <span className="font-medium text-white">{(currentPage - 1) * limit + 1}</span> to{' '}
                          <span className="font-medium text-white">
                            {Math.min(currentPage * limit, totalPlacements)}
                          </span>{' '}
                          of <span className="font-medium text-white">{totalPlacements}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <Button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center rounded-l-md px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-700 hover:bg-gray-800 focus:z-20 focus:outline-offset-0 disabled:opacity-30"
                          >
                            <span className="sr-only">Previous</span>
                            <FaChevronRight className="h-5 w-5 rotate-180" />
                          </Button>
                          {[...Array(totalPages)].map((_, i) => (
                            <Button
                              key={i + 1}
                              onClick={() => setCurrentPage(i + 1)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                                currentPage === i + 1
                                  ? 'z-10 bg-indigo-600 text-white focus-visible:outline-indigo-600'
                                  : 'text-gray-300 ring-1 ring-inset ring-gray-700 hover:bg-gray-800 focus:outline-offset-0'
                              }`}
                            >
                              {i + 1}
                            </Button>
                          ))}
                          <Button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center rounded-r-md px-3 py-2 text-gray-400 ring-1 ring-inset ring-gray-700 hover:bg-gray-800 focus:z-20 focus:outline-offset-0 disabled:opacity-30"
                          >
                            <span className="sr-only">Next</span>
                            <FaChevronRight className="h-5 w-5" />
                          </Button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreatePlacementModal
          projects={projects}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchData();
            if (onStatsUpdate) onStatsUpdate();
            showToast('Placement created successfully.');
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPlacement && (
        <EditPlacementModal
          placement={selectedPlacement}
          projects={projects}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPlacement(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedPlacement(null);
            fetchData();
            if (onStatsUpdate) onStatsUpdate();
            showToast('Placement updated successfully.');
          }}
        />
      )}

      {/* Video Modal */}
      <VideoModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        videoUrl="https://www.youtube.com/embed/WSvJyzmPy80"
        title="How to Create Internal Ads?"
      />

      {/* Upgrade Modal handled by parent App.js */}


    </div>
  );
};

// Create Placement Modal Component
const CreatePlacementModal = ({ projects, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    projectId: '',
    name: '',
    selectedProjects: [],
    settings: {
      displayFrequency: 'medium',
      maxImpressionsPerDay: 1000,
      maxClicksPerDay: 100,
      adSize: 'medium',
      position: 'bottom',
      theme: {
        primaryColor: '#3B82F6',
        backgroundColor: '#FFFFFF',
        textColor: '#1F2937'
      }
    }
  });
  const [loading, setLoading] = useState(false);

  // Get all projects except the selected project
  const availableProjects = projects.filter(project => project._id !== formData.projectId);

  // Auto-select all available projects when project is selected
  useEffect(() => {
    if (formData.projectId && availableProjects.length > 0) {
      setFormData(prev => ({
        ...prev,
        selectedProjects: availableProjects.map(project => project._id)
      }));
    }
  }, [formData.projectId, availableProjects.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authenticatedFetch('/api/internal-ads/placements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to create placement.');
      }
    } catch (error) {
      console.error('Error creating placement:', error);
      alert('Error occurred while creating placement.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
      <div className="relative top-0 mx-auto p-4 md:p-8 border w-full min-h-screen shadow-2xl bg-gray-900 border-gray-800">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-white">Create New Placement</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-200"
            >
              <FaTimes />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 text-left">Project</label>
              <select
                value={formData.projectId}
                onChange={(e) => setFormData({ ...formData, projectId: e.target.value, selectedProjects: [] })}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              >
                <option value="">Select project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 text-left">Placement Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter placement name"
                required
              />
            </div>

            {formData.projectId && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 text-left">
                  Projects to Show in Ads
                </label>
                <p className="text-sm text-gray-400 mb-4 text-left">
                  Select which projects should be advertised in this placement. The current project will not be shown.
                </p>
                <div className="max-h-60 overflow-y-auto bg-gray-700 rounded-lg border border-gray-600 p-4 space-y-3">
                  {availableProjects.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-4">No other projects available to advertise.</p>
                  ) : (
                      availableProjects.map((project) => (
                        <div key={project._id} className="p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors border border-gray-700/50">
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id={`project-${project._id}`}
                              checked={formData.selectedProjects.includes(project._id)}
                              onChange={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  selectedProjects: prev.selectedProjects.includes(project._id)
                                    ? prev.selectedProjects.filter(id => id !== project._id)
                                    : [...prev.selectedProjects, project._id]
                                }));
                              }}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              {project.logo ? (
                                <img
                                  src={project.logo.startsWith('http') ? project.logo : `${getBaseUrl()}${project.logo}`}
                                  alt={project.name}
                                  className="h-8 w-8 rounded-md object-cover border border-gray-600 flex-shrink-0"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="h-8 w-8 rounded-md bg-gray-700 flex items-center justify-center text-gray-400 text-xs border border-gray-600 flex-shrink-0">PRJ</div>
                              )}
                              <div className="flex flex-col min-w-0">
                                <label htmlFor={`project-${project._id}`} className="text-sm text-white cursor-pointer font-medium truncate">
                                  {project.name}
                                </label>
                                {project.website && (
                                  <div className="text-[10px] text-gray-400 flex items-center mt-0.5 truncate">
                                    <FaGlobe className="mr-1 h-2.5 w-2.5" />
                                    {project.website.replace(/^https?:\/\//, '')}
                                  </div>
                                )}
                              </div>
                            </div>
                            {formData.selectedProjects.includes(project._id) && (
                              <span className="text-[10px] text-green-400 uppercase font-bold px-1.5 py-0.5 bg-green-900/20 rounded border border-green-900/30">Selected</span>
                            )}
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || formData.selectedProjects.length === 0}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors font-medium"
              >
                {loading ? 'Creating...' : 'Create Placement'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Edit Placement Modal Component
const EditPlacementModal = ({ placement, projects, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: placement.name,
    description: placement.description,
    platform: placement.platform,
    selectedProducts: placement.selectedProducts?.map(p => p._id) || [],
    settings: placement.settings,
    platformStatus: placement.platformStatus || { android: true, ios: true, web: true }
  });
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (placement.projectId) {
      fetchProducts(placement.projectId._id);
    }
  }, [placement.projectId]);

  const fetchProducts = async (projectId) => {
    try {
      const response = await authenticatedFetch(`/api/dashboard/user/projects/${projectId}/products`);
      if (response.ok) {
        const data = await response.json();
        setAvailableProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authenticatedFetch(`/api/internal-ads/placements/${placement._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to update placement.');
      }
    } catch (error) {
      console.error('Error updating placement:', error);
      alert('Error occurred while updating placement.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg max-w-md w-full p-6 border border-gray-700">
        <h3 className="text-lg font-medium text-white mb-4">Edit Placement</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 block w-full bg-gray-700 border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="mt-1 block w-full bg-gray-700 border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-white"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300">Platform</label>
            <select
              value={formData.platform}
              onChange={(e) => {
                const newPlatform = e.target.value;
                // Clear selected products that don't match the new platform
                const filteredProducts = formData.selectedProducts.filter(productId => {
                  if (newPlatform === 'all') return true;
                  const product = availableProducts.find(p => p._id === productId);
                  return product && product.platform === newPlatform;
                });
                setFormData({ 
                  ...formData, 
                  platform: newPlatform,
                  selectedProducts: filteredProducts
                });
              }}
              className="mt-1 block w-full bg-gray-700 border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-white"
            >
              <option value="all">All Platforms</option>
              <option value="android">Android</option>
              <option value="ios">iOS</option>
              <option value="web">Web</option>
            </select>
          </div>

          <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600 space-y-2 text-left">
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Platform Active/Passive Toggles
            </label>
            <div className="flex flex-col space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.platformStatus?.android ?? true}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    platformStatus: { ...prev.platformStatus, android: e.target.checked }
                  }))}
                  className="rounded border-gray-650 text-indigo-600 focus:ring-indigo-500 bg-gray-850"
                />
                <span className="text-sm text-gray-300">Active on Android</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.platformStatus?.ios ?? true}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    platformStatus: { ...prev.platformStatus, ios: e.target.checked }
                  }))}
                  className="rounded border-gray-650 text-indigo-600 focus:ring-indigo-500 bg-gray-850"
                />
                <span className="text-sm text-gray-300">Active on iOS</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.platformStatus?.web ?? true}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    platformStatus: { ...prev.platformStatus, web: e.target.checked }
                  }))}
                  className="rounded border-gray-650 text-indigo-600 focus:ring-indigo-500 bg-gray-850"
                />
                <span className="text-sm text-gray-300">Active on Web</span>
              </label>
            </div>
          </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">Products to Show in Ads</label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {availableProducts
                  .filter(product => {
                    // Filter products based on selected platform
                    if (formData.platform === 'all') return true;
                    return product.platform === formData.platform;
                  })
                  .map((product) => (
                  <label key={product._id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.selectedProducts.includes(product._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            selectedProducts: [...formData.selectedProducts, product._id]
                          });
                        } else {
                          setFormData({
                            ...formData,
                            selectedProducts: formData.selectedProducts.filter(id => id !== product._id)
                          });
                        }
                      }}
                      className="rounded border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 bg-gray-700"
                    />
                    <span className="ml-2 text-sm text-gray-300">
                      {product.name}
                    </span>
                  </label>
                ))}
              </div>
              {availableProducts.filter(product => {
                if (formData.platform === 'all') return true;
                return product.platform === formData.platform;
              }).length === 0 && (
                <p className="text-sm text-gray-400">
                  {formData.platform === 'all' 
                    ? 'No products in this project yet.' 
                    : `No ${formData.platform} products in this project yet.`
                  }
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || formData.selectedProducts.length === 0}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </form>
      </div>
    </div>
  );
};


export default InternalAds;
